import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { View } from '../types';

// --- ICONS ---
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>;
const CheckCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>;

// --- REUSABLE COMPONENTS ---
const Section: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
    <section className="mb-8">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2">{title}</h2>
        <div className="bg-[#15243E] rounded-xl border border-blue-900/50 overflow-hidden">
            {children}
        </div>
    </section>
);
const ListItem: React.FC<{ children: React.ReactNode, isLast?: boolean }> = ({ children, isLast }) => (
    <div className={`flex items-center justify-between p-4 ${!isLast ? "border-b border-blue-900/50" : ""}`}>
        {children}
    </div>
);

interface SubscriptionManagementProps {
    onNavigate: (view: View) => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ onNavigate }) => {
    const { user, subscription, redirectToCustomerPortal, products } = useAuth();

    const handleManageSubscription = () => {
        if (subscription?.plan === 'Free' || subscription?.plan === 'Trial') {
            onNavigate('pricing');
        } else {
            redirectToCustomerPortal();
        }
    };

    const planName = subscription?.plan || 'N/A';
    
    const currentProduct = products.find(p => p.role?.toLowerCase() === planName.toLowerCase());
    const monthlyPrice = currentProduct?.prices?.find(p => p.interval === 'month');
    const planPrice = monthlyPrice ? `$${(monthlyPrice.unit_amount / 100).toFixed(2)}/mo` : 'Free';

    return (
        <div>
            <header className="mb-8 mt-6">
                <button onClick={() => onNavigate('account')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold mb-4">
                    <BackIcon /> Back to Settings
                </button>
                <h1 className="text-3xl font-bold">Subscription & Billing</h1>
            </header>

            <Section title="User Account">
                <ListItem isLast>
                    <div className="flex items-center gap-4">
                        <UserIcon />
                        <div>
                            <p className="font-semibold text-white">{user?.name || 'Clinical User'}</p>
                            <p className="text-sm text-gray-400">{user?.email || 'No email provided'}</p>
                        </div>
                    </div>
                </ListItem>
            </Section>

            <Section title="Current Plan">
                <div className="p-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <h3 className="text-2xl font-bold text-white">{planName} Plan</h3>
                            <p className="text-gray-400 mt-1">{planPrice}</p>
                        </div>
                        <button onClick={handleManageSubscription} className={planName === 'Free' || planName === 'Trial' ? 'bg-yellow-400 text-gray-900 font-bold py-2 px-4 rounded-lg hover:bg-yellow-300 transition-transform hover:scale-105' : 'bg-blue-800/80 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700/80 transition-colors'}>
                            {planName === 'Free' || planName === 'Trial' ? 'Upgrade Plan' : 'Manage Billing'}
                        </button>
                    </div>
                    <ul className="space-y-2 text-sm mt-6 pl-1">
                        {(currentProduct?.features || []).map(feature => (
                            <li key={feature} className="flex items-center gap-3">
                                <CheckCircleIcon />
                                <span className="text-gray-300">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Section>

             <Section title="Billing History">
                <div className="p-10 text-center text-gray-500">
                    <p>Billing history is managed in your Stripe customer portal.</p>
                     <p className="mt-1">Click 'Manage Billing' above to view invoices.</p>
                </div>
            </Section>
        </div>
    );
}

export default SubscriptionManagement;
